import React, { useEffect, useState, memo, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { TrendingUp } from 'lucide-react';
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ReferenceLine,
} from 'recharts';
import { supabase } from '@/integrations/supabase/client';

interface IntradayChartProps {
  symbol: string;
  compact?: boolean;
}

interface Point {
  t: number;
  p: number;
}

interface QuoteData {
  previousClose: number;
  last: number;
  changePct: number;
  points: Point[];
}

const formatTime = (t: number) =>
  new Date(t).toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    timeZone: 'America/New_York',
  });

const IntradayChart: React.FC<IntradayChartProps> = memo(({ symbol, compact = false }) => {
  const [data, setData] = useState<QuoteData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    try {
      const { data: res, error: fnError } = await supabase.functions.invoke(
        `intraday-quote?symbol=${encodeURIComponent(symbol)}`,
        { method: 'GET' }
      );
      if (fnError) throw fnError;
      if (!res || res.error) throw new Error(res?.error || 'No data');
      setData(res as QuoteData);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load chart');
    } finally {
      setLoading(false);
    }
  }, [symbol]);

  useEffect(() => {
    setLoading(true);
    setData(null);
    fetchData();
    const interval = setInterval(fetchData, 60000);
    return () => clearInterval(interval);
  }, [fetchData]);

  const isPositive = (data?.changePct ?? 0) >= 0;
  const lineColor = isPositive ? '#22c55e' : '#dc2626';

  const prices = data?.points.map((d) => d.p) ?? [];
  const previousClose = data?.previousClose ?? 0;
  const min = prices.length ? Math.min(...prices, previousClose) : 0;
  const max = prices.length ? Math.max(...prices, previousClose) : 0;
  const pad = (max - min) * 0.1 || 1;

  if (compact) {
    return (
      <div className="min-w-0 border-t border-delphi-gray/20 pt-3 lg:border-l lg:border-t-0 lg:pl-4 lg:pt-0">
        <div className="flex items-center gap-2 mb-2 flex-wrap">
          <TrendingUp size={15} className="text-delphi-red flex-shrink-0" />
          <span className="text-sm font-medium text-delphi-light">{symbol} Intraday</span>
          {data && (
            <span className="text-xs font-medium" style={{ color: lineColor }}>
              ${data.last.toFixed(2)} ({isPositive ? '+' : ''}{data.changePct.toFixed(2)}%)
            </span>
          )}
        </div>
        <div className="w-full h-[150px] sm:h-[165px] 2xl:h-[140px]">
          {loading ? (
            <div className="h-full flex items-center justify-center text-delphi-light/50 text-xs">Loading chart...</div>
          ) : error || !data || data.points.length === 0 ? (
            <div className="h-full flex items-center justify-center text-delphi-light/50 text-xs">No intraday data available</div>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={data.points} margin={{ top: 8, right: 4, left: -22, bottom: 0 }}>
                <defs>
                  <linearGradient id={`fill-${symbol}-compact`} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={lineColor} stopOpacity={0.25} />
                    <stop offset="100%" stopColor={lineColor} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke="hsl(var(--muted-foreground))" strokeOpacity={0.15} strokeDasharray="3 3" />
                <XAxis dataKey="t" tickFormatter={formatTime} minTickGap={55} tick={{ fontSize: 9, fill: 'hsl(var(--muted-foreground))' }} stroke="hsl(var(--muted-foreground))" strokeOpacity={0.3} />
                <YAxis domain={[min - pad, max + pad]} tickFormatter={(v: number) => `$${v.toFixed(2)}`} width={52} tick={{ fontSize: 9, fill: 'hsl(var(--muted-foreground))' }} stroke="hsl(var(--muted-foreground))" strokeOpacity={0.3} />
                <Tooltip contentStyle={{ background: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: 8, fontSize: 10 }} labelFormatter={(l: number) => formatTime(l)} formatter={(v: number) => [`$${v.toFixed(2)}`, 'Price']} />
                <ReferenceLine y={data.previousClose} stroke="hsl(var(--muted-foreground))" strokeOpacity={0.5} strokeDasharray="4 4" />
                <Area type="monotone" dataKey="p" stroke={lineColor} strokeWidth={2} fill={`url(#fill-${symbol}-compact)`} dot={false} isAnimationActive={false} />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>
    );
  }

  return (
    <Card className="bg-gradient-to-br from-delphi-dark to-delphi-dark/80 border border-delphi-red/30 shadow-lg shadow-delphi-red/5 h-full flex flex-col">
      <CardHeader className="pb-2 flex-shrink-0">
        <CardTitle className="text-base sm:text-lg text-delphi-light flex items-center gap-2 flex-wrap">
          <TrendingUp size={18} className="text-delphi-red flex-shrink-0" />
          <span>{symbol} Intraday</span>
          {data && (
            <span
              className="text-sm font-medium"
              style={{ color: lineColor }}
            >
              ${data.last.toFixed(2)} ({isPositive ? '+' : ''}
              {data.changePct.toFixed(2)}%)
            </span>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-0 flex-1 min-h-[240px] sm:min-h-[270px] lg:min-h-[clamp(260px,32vh,360px)]">
        <div className="w-full h-full min-h-[220px] sm:min-h-[250px] lg:min-h-[clamp(240px,30vh,340px)]">
          {loading ? (
            <div className="h-full flex items-center justify-center text-delphi-light/50 text-sm">
              Loading chart...
            </div>
          ) : error || !data || data.points.length === 0 ? (
            <div className="h-full flex items-center justify-center text-delphi-light/50 text-sm">
              No intraday data available
            </div>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={data.points} margin={{ top: 10, right: 8, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id={`fill-${symbol}`} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={lineColor} stopOpacity={0.25} />
                    <stop offset="100%" stopColor={lineColor} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke="hsl(var(--muted-foreground))" strokeOpacity={0.15} strokeDasharray="3 3" />
                <XAxis
                  dataKey="t"
                  tickFormatter={formatTime}
                  minTickGap={40}
                  tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }}
                  stroke="hsl(var(--muted-foreground))"
                  strokeOpacity={0.3}
                />
                <YAxis
                  domain={[min - pad, max + pad]}
                  tickFormatter={(v: number) => `$${v.toFixed(2)}`}
                  width={62}
                  tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }}
                  stroke="hsl(var(--muted-foreground))"
                  strokeOpacity={0.3}
                />
                <Tooltip
                  contentStyle={{
                    background: 'hsl(var(--card))',
                    border: '1px solid hsl(var(--border))',
                    borderRadius: 8,
                    fontSize: 12,
                  }}
                  labelFormatter={(l: number) => formatTime(l)}
                  formatter={(v: number) => [`$${v.toFixed(2)}`, 'Price']}
                />
                <ReferenceLine
                  y={data.previousClose}
                  stroke="hsl(var(--muted-foreground))"
                  strokeOpacity={0.5}
                  strokeDasharray="4 4"
                />
                <Area
                  type="monotone"
                  dataKey="p"
                  stroke={lineColor}
                  strokeWidth={2}
                  fill={`url(#fill-${symbol})`}
                  dot={false}
                  isAnimationActive={false}
                />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </div>
      </CardContent>
    </Card>
  );
});

IntradayChart.displayName = 'IntradayChart';

export default IntradayChart;
